(() => {
  if (window.__awRankingProductsLoaded) {
    return;
  }

  window.__awRankingProductsLoaded = true;

  const instances = new Map();
  const sliderUtils = window.AWSliderCoreUtils;
  const selectors = {
    root: "[data-aw-ranking-products]",
    tab: "[data-aw-ranking-tab]",
    panel: "[data-aw-ranking-panel]",
    viewport: "[data-aw-ranking-viewport]",
    list: "[data-aw-ranking-list]",
    item: "[data-aw-ranking-item]",
    scrollbar: "[data-aw-ranking-scrollbar]",
    thumb: "[data-aw-ranking-scrollbar-thumb]",
    prev: "[data-aw-ranking-prev]",
    next: "[data-aw-ranking-next]",
  };

  class AWRankingProducts {
    constructor(root) {
      this.root = root;
      this.sectionId = root.dataset.sectionId || "";
      this.tabs = Array.from(root.querySelectorAll(selectors.tab));
      this.panels = Array.from(root.querySelectorAll(selectors.panel)).map((panel) => this.createPanelState(panel));
      this.teardownResizeObserver = null;
      this.activeIndex = this.getInitialIndex();

      this.onTabClick = this.handleTabClick.bind(this);
      this.onTabKeydown = this.handleTabKeydown.bind(this);
      this.onObservedResize = this.updateVisiblePanel.bind(this);

      this.bindTabEvents();
      this.bindPanelEvents();
      this.bindResizeObserver();
      this.activate(this.activeIndex, { focus: false, resetScroll: false });
    }

    createPanelState(panel) {
      return {
        panel,
        viewport: panel.querySelector(selectors.viewport),
        list: panel.querySelector(selectors.list),
        items: Array.from(panel.querySelectorAll(selectors.item)),
        images: Array.from(panel.querySelectorAll("img")),
        scrollbar: panel.querySelector(selectors.scrollbar),
        thumb: panel.querySelector(selectors.thumb),
        prevButton: panel.querySelector(selectors.prev),
        nextButton: panel.querySelector(selectors.next),
        rafId: null,
        pointerId: null,
        startX: 0,
        startScrollLeft: 0,
        hasDragged: false,
        preventClick: false,
      };
    }

    getInitialIndex() {
      const initialIndex = Number.parseInt(this.root.dataset.initialTabIndex || "1", 10) - 1;

      if (Number.isInteger(initialIndex) && initialIndex >= 0 && initialIndex < this.tabs.length) {
        return initialIndex;
      }

      return 0;
    }

    bindTabEvents() {
      this.tabs.forEach((tab) => {
        tab.addEventListener("click", this.onTabClick);
        tab.addEventListener("keydown", this.onTabKeydown);
      });
    }

    bindPanelEvents() {
      this.panels.forEach((state) => {
        if (!state.viewport) {
          return;
        }

        state.onScroll = () => this.requestPanelUpdate(state);
        state.onPrevClick = () => {
          if (state.prevButton && state.prevButton.getAttribute("aria-disabled") === "true") {
            return;
          }

          this.scrollPanelBy(state, this.getScrollAmount(state) * -2);
        };
        state.onNextClick = () => {
          if (state.nextButton && state.nextButton.getAttribute("aria-disabled") === "true") {
            return;
          }

          this.scrollPanelBy(state, this.getScrollAmount(state) * 2);
        };
        state.onPointerDown = (event) => this.handlePointerDown(state, event);
        state.onPointerMove = (event) => this.handlePointerMove(state, event);
        state.onPointerUp = (event) => this.handlePointerEnd(state, event);
        state.onPointerCancel = (event) => this.handlePointerEnd(state, event);
        state.onClickCapture = (event) => this.handleViewportClick(state, event);
        state.onImageLoad = () => this.requestPanelUpdate(state);

        state.viewport.addEventListener("scroll", state.onScroll, { passive: true });
        state.viewport.addEventListener("pointerdown", state.onPointerDown);
        state.viewport.addEventListener("pointermove", state.onPointerMove);
        state.viewport.addEventListener("pointerup", state.onPointerUp);
        state.viewport.addEventListener("pointercancel", state.onPointerCancel);
        state.viewport.addEventListener("click", state.onClickCapture, true);

        if (state.prevButton) {
          state.prevButton.addEventListener("click", state.onPrevClick);
        }

        if (state.nextButton) {
          state.nextButton.addEventListener("click", state.onNextClick);
        }

        state.images.forEach((image) => {
          if (image.complete) {
            return;
          }

          image.addEventListener("load", state.onImageLoad, { once: true });
        });
      });
    }

    bindResizeObserver() {
      this.onWindowResize = null;
      this.teardownResizeObserver = sliderUtils.observeResize(
        [
          this.root,
          ...this.panels.map((state) => state.viewport),
          ...this.panels.map((state) => state.list),
        ],
        this.onObservedResize
      );
    }

    handleTabClick(event) {
      const index = this.tabs.indexOf(event.currentTarget);

      if (index >= 0) {
        this.activate(index, { resetScroll: true });
      }
    }

    handleTabKeydown(event) {
      const index = this.tabs.indexOf(event.currentTarget);

      if (index < 0) {
        return;
      }

      let nextIndex = index;

      switch (event.key) {
        case "ArrowRight":
          nextIndex = (index + 1) % this.tabs.length;
          break;
        case "ArrowLeft":
          nextIndex = (index - 1 + this.tabs.length) % this.tabs.length;
          break;
        case "Home":
          nextIndex = 0;
          break;
        case "End":
          nextIndex = this.tabs.length - 1;
          break;
        case "Enter":
        case " ":
          event.preventDefault();
          this.activate(index, { resetScroll: true });
          return;
        default:
          return;
      }

      event.preventDefault();
      this.activate(nextIndex, { focus: true, resetScroll: true });
    }

    activate(index, options = {}) {
      const { focus = false, resetScroll = true } = options;

      if (!this.tabs.length || index < 0 || index >= this.tabs.length) {
        return;
      }

      this.activeIndex = index;

      this.tabs.forEach((tab, tabIndex) => {
        const isActive = tabIndex === index;

        tab.classList.toggle("is-active", isActive);
        tab.setAttribute("aria-selected", isActive ? "true" : "false");
        tab.setAttribute("tabindex", isActive ? "0" : "-1");
      });

      this.panels.forEach((state, panelIndex) => {
        const isActive = panelIndex === index;

        state.panel.classList.toggle("is-active", isActive);
        state.panel.hidden = !isActive;
      });

      const activePanel = this.panels[index];

      if (activePanel && activePanel.viewport && resetScroll) {
        activePanel.viewport.scrollTo({ left: 0, behavior: "auto" });
      }

      window.requestAnimationFrame(() => {
        this.updateVisiblePanel();
        this.updateNavyHeight();
      });

      if (focus) {
        this.tabs[index].focus();
      }
    }

    getVisiblePanel() {
      return this.panels[this.activeIndex] || null;
    }

    getMaxScroll(state) {
      if (!state || !state.viewport) {
        return 0;
      }

      return sliderUtils.getMaxScroll(state.viewport);
    }

    getScrollAmount(state) {
      return sliderUtils.getItemDistance({
        list: state.list,
        items: state.items,
        itemSelector: selectors.item,
        viewport: state.viewport,
      });
    }

    getScrollBehavior() {
      return sliderUtils.getScrollBehavior();
    }

    updateButtonState(button, isDisabled) {
      sliderUtils.updateButtonState(button, isDisabled);
    }

    scrollPanelBy(state, amount) {
      if (!state || !state.viewport) {
        return;
      }

      state.viewport.scrollBy({
        left: amount,
        behavior: this.getScrollBehavior(),
      });
    }

    requestPanelUpdate(state) {
      if (!state || state.rafId) {
        return;
      }

      state.rafId = window.requestAnimationFrame(() => {
        state.rafId = null;
        this.updatePanel(state);
      });
    }

    updatePanel(state) {
      if (!state || !state.viewport) {
        return;
      }

      const maxScroll = this.getMaxScroll(state);
      const canScroll = maxScroll > 1;
      const scrollLeft = Math.max(0, state.viewport.scrollLeft);
      const atStart = scrollLeft <= 1;
      const atEnd = scrollLeft >= maxScroll - 1;

      this.updateButtonState(state.prevButton, !canScroll || atStart);
      this.updateButtonState(state.nextButton, !canScroll || atEnd);

      if (state.scrollbar) {
        state.scrollbar.classList.toggle("is-hidden", !canScroll);
      }

      sliderUtils.updateScrollbar({
        viewport: state.viewport,
        scrollbar: state.scrollbar,
        thumb: state.thumb,
        enabled: canScroll,
      });
    }

    updateVisiblePanel() {
      const activePanel = this.getVisiblePanel();

      if (activePanel) {
        this.updatePanel(activePanel);
      }

      this.updateNavyHeight();
    }

    updateNavyHeight() {
      const activePanel = this.getVisiblePanel();
      const imageWrap = activePanel && activePanel.panel.querySelector(".aw-product-card__image-wrap");

      if (!imageWrap) {
        return;
      }

      const rootRect = this.root.getBoundingClientRect();
      const imageRect = imageWrap.getBoundingClientRect();
      const height = imageRect.top - rootRect.top + imageRect.height / 2;

      if (Number.isFinite(height) && height >= 0) {
        this.root.style.setProperty("--aw-ranking-navy-height-current", `${height}px`);
      }
    }

    handlePointerDown(state, event) {
      if (!state.viewport || event.pointerType !== "mouse" || event.button !== 0 || this.getMaxScroll(state) <= 1) {
        return;
      }

      state.pointerId = event.pointerId;
      state.startX = event.clientX;
      state.startScrollLeft = state.viewport.scrollLeft;
      state.hasDragged = false;
    }

    handlePointerMove(state, event) {
      if (!state.viewport || state.pointerId !== event.pointerId) {
        return;
      }

      const delta = event.clientX - state.startX;

      if (!state.hasDragged && Math.abs(delta) > 5) {
        state.hasDragged = true;
        state.viewport.classList.add("is-dragging");

        // Delay pointer capture until an actual drag starts so regular link clicks still navigate.
        if (typeof state.viewport.setPointerCapture === "function") {
          state.viewport.setPointerCapture(event.pointerId);
        }
      }

      if (!state.hasDragged) {
        return;
      }

      event.preventDefault();
      state.viewport.scrollLeft = state.startScrollLeft - delta;
      this.requestPanelUpdate(state);
    }

    handlePointerEnd(state, event) {
      if (!state.viewport || state.pointerId !== event.pointerId) {
        return;
      }

      if (typeof state.viewport.releasePointerCapture === "function" && state.viewport.hasPointerCapture(event.pointerId)) {
        state.viewport.releasePointerCapture(event.pointerId);
      }

      if (state.hasDragged) {
        state.preventClick = true;
        window.setTimeout(() => {
          state.preventClick = false;
        }, 300);
      }

      state.pointerId = null;
      state.hasDragged = false;
      state.viewport.classList.remove("is-dragging");
    }

    handleViewportClick(state, event) {
      if (!state.preventClick) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      state.preventClick = false;
    }

    selectBlock(blockId, fallbackTarget) {
      let tab = blockId ? this.root.querySelector(`${selectors.tab}[data-block-id="${blockId}"]`) : null;

      if (!tab && fallbackTarget && typeof fallbackTarget.closest === "function") {
        tab = fallbackTarget.closest(selectors.tab) || fallbackTarget.closest(selectors.panel);
      }

      const resolvedBlockId = tab && tab.dataset ? tab.dataset.blockId : blockId;
      const index = resolvedBlockId ? this.tabs.findIndex((button) => button.dataset.blockId === resolvedBlockId) : -1;

      if (index >= 0) {
        this.activate(index, { resetScroll: true });
      }
    }

    destroy() {
      this.tabs.forEach((tab) => {
        tab.removeEventListener("click", this.onTabClick);
        tab.removeEventListener("keydown", this.onTabKeydown);
      });

      this.panels.forEach((state) => {
        if (state.rafId) {
          window.cancelAnimationFrame(state.rafId);
          state.rafId = null;
        }

        if (!state.viewport) {
          return;
        }

        state.viewport.removeEventListener("scroll", state.onScroll);
        state.viewport.removeEventListener("pointerdown", state.onPointerDown);
        state.viewport.removeEventListener("pointermove", state.onPointerMove);
        state.viewport.removeEventListener("pointerup", state.onPointerUp);
        state.viewport.removeEventListener("pointercancel", state.onPointerCancel);
        state.viewport.removeEventListener("click", state.onClickCapture, true);

        if (state.prevButton) {
          state.prevButton.removeEventListener("click", state.onPrevClick);
        }

        if (state.nextButton) {
          state.nextButton.removeEventListener("click", state.onNextClick);
        }

        state.images.forEach((image) => {
          image.removeEventListener("load", state.onImageLoad);
        });
      });

      if (this.teardownResizeObserver) {
        this.teardownResizeObserver();
        this.teardownResizeObserver = null;
      }
    }
  }

  function getRoot(target) {
    if (!target) {
      return null;
    }

    if (typeof target.matches === "function" && target.matches(selectors.root)) {
      return target;
    }

    if (typeof target.querySelector === "function") {
      const root = target.querySelector(selectors.root);

      if (root) {
        return root;
      }
    }

    if (typeof target.closest === "function") {
      return target.closest(selectors.root);
    }

    return null;
  }

  function destroySection(sectionId) {
    const instance = instances.get(sectionId);

    if (!instance) {
      return;
    }

    instance.destroy();
    instances.delete(sectionId);
  }

  function initSection(root) {
    if (!root) {
      return;
    }

    const sectionId = root.dataset.sectionId;

    if (!sectionId) {
      return;
    }

    destroySection(sectionId);
    instances.set(sectionId, new AWRankingProducts(root));
  }

  function initAll(container = document) {
    container.querySelectorAll(selectors.root).forEach((root) => {
      initSection(root);
    });
  }

  document.addEventListener("shopify:section:load", (event) => {
    initSection(getRoot(event.target));
  });

  document.addEventListener("shopify:section:unload", (event) => {
    const root = getRoot(event.target);

    if (!root) {
      return;
    }

    destroySection(root.dataset.sectionId);
  });

  document.addEventListener("shopify:block:select", (event) => {
    const sectionId = event.detail && event.detail.sectionId;
    const blockId = event.detail && event.detail.blockId;
    const instance = sectionId ? instances.get(sectionId) : null;

    if (!instance) {
      return;
    }

    instance.selectBlock(blockId, event.target);
  });

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      initAll(document);
    });
  } else {
    initAll(document);
  }
})();
